import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Calendar, Clock, User, ArrowLeft, ArrowRight, Share2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import axios from 'axios';
import { API } from '@/App';

const BlogArticlePage = () => {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [relatedArticles, setRelatedArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/blog/${slug}`)
      .then(res => {
        setArticle(res.data);
        document.title = res.data.meta_title || res.data.title;
        const metaDesc = document.querySelector('meta[name="description"]');
        if (metaDesc) metaDesc.setAttribute('content', res.data.meta_description || res.data.excerpt);
      })
      .catch(() => setArticle(null))
      .finally(() => setLoading(false));

    axios.get(`${API}/blog`)
      .then(res => setRelatedArticles(res.data.filter(a => a.slug !== slug).slice(0, 3)))
      .catch(() => {});
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-slate-900 mb-4">Artikel nicht gefunden</h1>
          <Link to="/blog">
            <Button>Zurück zum Blog</Button>
          </Link>
        </div>
      </div>
    );
  }

  // Enhanced markdown parser
  const parseContent = (content) => {
    if (!content) return '';
    
    let html = content;
    
    // Process line by line for better control
    const lines = html.split('\n');
    const result = [];
    let inList = false;
    let listType = null;
    let inTable = false;
    let tableStarted = false;
    
    for (let i = 0; i < lines.length; i++) {
      let line = lines[i];
      
      // Skip empty lines but close lists/tables
      if (line.trim() === '') {
        if (inList) {
          result.push(`</${listType}>`);
          inList = false;
          listType = null;
        }
        if (inTable) {
          result.push('</tbody></table>');
          inTable = false;
          tableStarted = false;
        }
        continue;
      }
      
      // Headers (process before other replacements)
      if (line.startsWith('#### ')) {
        if (inList) { result.push(`</${listType}>`); inList = false; }
        if (inTable) { result.push('</tbody></table>'); inTable = false; }
        result.push(`<h4 class="text-lg font-semibold text-slate-900 mt-6 mb-3">${line.slice(5)}</h4>`);
        continue;
      }
      if (line.startsWith('### ')) {
        if (inList) { result.push(`</${listType}>`); inList = false; }
        if (inTable) { result.push('</tbody></table>'); inTable = false; }
        result.push(`<h3 class="text-xl font-semibold text-slate-900 mt-8 mb-4">${line.slice(4)}</h3>`);
        continue;
      }
      if (line.startsWith('## ')) {
        if (inList) { result.push(`</${listType}>`); inList = false; }
        if (inTable) { result.push('</tbody></table>'); inTable = false; }
        result.push(`<h2 class="text-2xl font-bold text-slate-900 mt-10 mb-4">${line.slice(3)}</h2>`);
        continue;
      }
      if (line.startsWith('# ')) {
        if (inList) { result.push(`</${listType}>`); inList = false; }
        if (inTable) { result.push('</tbody></table>'); inTable = false; }
        result.push(`<h2 class="text-2xl font-bold text-slate-900 mt-10 mb-4">${line.slice(2)}</h2>`);
        continue;
      }
      
      // Table separator row (skip)
      if (line.includes('|') && line.includes('---')) {
        continue;
      }
      
      // Table rows
      if (line.trim().startsWith('|') && line.trim().endsWith('|')) {
        if (inList) { result.push(`</${listType}>`); inList = false; }
        const cells = line.split('|').filter(c => c.trim());
        if (!inTable) {
          result.push('<div class="overflow-x-auto my-6"><table class="w-full border-collapse border border-slate-200 rounded-lg">');
          inTable = true;
        }
        if (!tableStarted) {
          result.push('<thead class="bg-slate-50"><tr>');
          cells.forEach(c => result.push(`<th class="border border-slate-200 px-4 py-3 text-left font-semibold text-slate-700">${c.trim()}</th>`));
          result.push('</tr></thead><tbody>');
          tableStarted = true;
        } else {
          result.push('<tr>');
          cells.forEach(c => result.push(`<td class="border border-slate-200 px-4 py-3 text-slate-600">${c.trim()}</td>`));
          result.push('</tr>');
        }
        continue;
      }
      
      // Unordered list items
      if (line.trim().startsWith('- ') || line.trim().startsWith('* ')) {
        if (inTable) { result.push('</tbody></table></div>'); inTable = false; tableStarted = false; }
        const listContent = line.trim().slice(2);
        if (!inList || listType !== 'ul') {
          if (inList) result.push(`</${listType}>`);
          result.push('<ul class="list-disc pl-6 mb-4 space-y-2">');
          inList = true;
          listType = 'ul';
        }
        // Process inline formatting
        const formattedContent = listContent
          .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
          .replace(/\*(.*?)\*/g, '<em>$1</em>')
          .replace(/✅/g, '<span class="text-green-600">✅</span>')
          .replace(/❌/g, '<span class="text-red-600">❌</span>');
        result.push(`<li class="text-slate-700">${formattedContent}</li>`);
        continue;
      }
      
      // Ordered list items
      if (/^\d+\.\s/.test(line.trim())) {
        if (inTable) { result.push('</tbody></table></div>'); inTable = false; tableStarted = false; }
        const listContent = line.trim().replace(/^\d+\.\s/, '');
        if (!inList || listType !== 'ol') {
          if (inList) result.push(`</${listType}>`);
          result.push('<ol class="list-decimal pl-6 mb-4 space-y-2">');
          inList = true;
          listType = 'ol';
        }
        const formattedContent = listContent
          .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
          .replace(/\*(.*?)\*/g, '<em>$1</em>');
        result.push(`<li class="text-slate-700">${formattedContent}</li>`);
        continue;
      }
      
      // Close lists if we're in a paragraph
      if (inList) {
        result.push(`</${listType}>`);
        inList = false;
        listType = null;
      }
      if (inTable) {
        result.push('</tbody></table></div>');
        inTable = false;
        tableStarted = false;
      }
      
      // Regular paragraph with inline formatting
      let paragraph = line
        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
        .replace(/\*(.*?)\*/g, '<em>$1</em>')
        .replace(/✅/g, '<span class="text-green-600">✅</span>')
        .replace(/❌/g, '<span class="text-red-600">❌</span>');
      
      result.push(`<p class="mb-4 text-slate-700 leading-relaxed">${paragraph}</p>`);
    }
    
    // Close any open tags
    if (inList) result.push(`</${listType}>`);
    if (inTable) result.push('</tbody></table></div>');
    
    return result.join('');
  };

  return (
    <div className="pt-20" data-testid="blog-article-page">
      {/* Header */}
      <section className="py-8 bg-slate-50 border-b">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/blog" className="inline-flex items-center text-blue-600 hover:text-blue-700 mb-6">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Zurück zum Blog
          </Link>
        </div>
      </section>

      {/* Article */}
      <article className="py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Featured Image */}
          <div className="relative h-64 md:h-96 rounded-2xl overflow-hidden mb-8">
            <img 
              src={article.image_url} 
              alt={article.title}
              className="w-full h-full object-cover"
            />
          </div>

          {/* Meta */}
          <div className="flex flex-wrap items-center gap-4 text-sm text-slate-500 mb-6">
            <div className="flex items-center gap-2">
              <User className="w-4 h-4" />
              {article.author}
            </div>
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              {new Date(article.created_at).toLocaleDateString('de-DE', { 
                day: '2-digit', 
                month: 'long', 
                year: 'numeric' 
              })}
            </div>
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4" />
              5 Min. Lesezeit
            </div>
          </div>

          {/* Title */}
          <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6" data-testid="article-title">
            {article.title}
          </h1>

          {/* Excerpt */}
          <p className="text-xl text-slate-600 mb-8 leading-relaxed">
            {article.excerpt}
          </p>

          {/* Content */}
          <div 
            className="prose prose-lg max-w-none"
            dangerouslySetInnerHTML={{ __html: `<p class="mb-4 text-slate-700 leading-relaxed">${parseContent(article.content)}</p>` }}
            data-testid="article-content"
          />

          {/* Tags */}
          {article.keywords && article.keywords.length > 0 && (
            <div className="mt-12 pt-8 border-t">
              <div className="flex flex-wrap gap-2">
                {article.keywords.map((keyword, index) => (
                  <span key={index} className="px-3 py-1 bg-slate-100 text-slate-600 rounded-full text-sm">
                    {keyword}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Share */}
          <div className="mt-8 flex items-center gap-4">
            <span className="text-slate-600">Teilen:</span>
            <Button variant="outline" size="sm" className="rounded-full">
              <Share2 className="w-4 h-4 mr-2" />
              Teilen
            </Button>
          </div>
        </div>
      </article>

      {/* CTA */}
      <section className="py-12 bg-blue-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl font-bold text-white mb-4">
            Bereit für deinen Führerschein?
          </h2>
          <p className="text-blue-100 mb-6">
            Vereinbare jetzt dein kostenloses Beratungsgespräch!
          </p>
          <form method="POST" target="_blank" action="https://api.fahrschulmanager.de/v1/onlineanmeldung/authenticate?t=8jT3GKqjQY4PQILDGcBblEvr%2F1EfNxqBcLlN0OrkAW7dww5L6e3pxd0HUN4PrKGX0CBTr0K7Qk3BrHvIWwm%2BAQ%3D%3D">
            <Button type="submit" size="lg" className="bg-white text-blue-600 hover:bg-blue-50 rounded-full px-8">
              Jetzt anmelden <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </form>
        </div>
      </section>

      {/* Related Articles */}
      {relatedArticles.length > 0 && (
        <section className="py-16 bg-slate-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-8">Weitere Artikel</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {relatedArticles.map((relatedArticle) => (
                <Link 
                  key={relatedArticle.slug} 
                  to={`/blog/${relatedArticle.slug}`}
                  className="blog-card group"
                >
                  <Card className="overflow-hidden h-full border-slate-200 hover:border-blue-200 hover:shadow-xl transition-all duration-300">
                    <div className="relative h-48 overflow-hidden">
                      <img 
                        src={relatedArticle.image_url} 
                        alt={relatedArticle.title}
                        className="blog-card-image w-full h-full object-cover"
                      />
                    </div>
                    <CardContent className="p-6">
                      <h3 className="text-lg font-semibold text-slate-900 group-hover:text-blue-600 transition-colors line-clamp-2">
                        {relatedArticle.title}
                      </h3>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Schema */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BlogPosting",
          "headline": article.title,
          "description": article.excerpt,
          "image": article.image_url,
          "author": {
            "@type": "Organization",
            "name": article.author
          },
          "publisher": {
            "@type": "Organization",
            "name": "Fahrschule Rößner GmbH",
            "logo": {
              "@type": "ImageObject",
              "url": "https://roessnerfahrschule.de/neu/wp-content/uploads/2025/04/cropped-Logo-Roessner.webp"
            }
          },
          "datePublished": article.created_at,
          "dateModified": article.updated_at
        })
      }} />
    </div>
  );
};

export default BlogArticlePage;
